/**
 * Lead-Paint Disclosure Agent — API client for the per-deal answers behind the
 * federal Lead-Based Paint Disclosure (pre-1978 or unknown-year homes).
 *
 * The seller answers what they know about lead-based paint and what records
 * they have; the buyer confirms receiving the EPA pamphlet. The backend does
 * not generate the disclosure until both are recorded, then generates it
 * itself; nothing can change once the agreement is sent.
 *
 * Backend contract:
 *   GET /api/offers/{offer_id}/lead-paint-disclosure
 *     -> { offer_id, required, locked, my_role,
 *          pamphlet: { title, edition, url, info_url },
 *          record: null | { hazards_answer: 'known'|'none', hazards_description,
 *            records_answer: 'provided'|'none', records_description,
 *            records_delivery_confirmed, agent_answer, seller_answered,
 *            seller_answered_at, pamphlet_received, pamphlet_received_at,
 *            pamphlet_edition, complete, ... } }
 *   PUT /api/offers/{offer_id}/lead-paint-disclosure/seller-answers
 *     body { hazards_answer, hazards_description?, records_answer,
 *            records_description?, records_delivery_confirmed, agent_answer,
 *            attested }
 *   PUT /api/offers/{offer_id}/lead-paint-disclosure/pamphlet-receipt
 *     body { received: true }
 *   POST /api/offers/{offer_id}/lead-paint-disclosure/regenerate
 *     (seller only) regenerates a disclosure the send check calls stale or
 *     unsignable; 503 `detail` when no signable one could be prepared.
 *   All writes return the GET shape. 422 `detail` on invalid answers (and
 *   for a seller with an agent); 409 `detail` once the agreement was sent.
 */

import APIAgent from './apiAgent';

export const HAZARDS = Object.freeze({ KNOWN: 'known', NONE: 'none' });
export const RECORDS = Object.freeze({ PROVIDED: 'provided', NONE: 'none' });
export const AGENT = Object.freeze({ NONE: 'none', AGENT: 'agent' });

const base = (offerId) => `/api/offers/${encodeURIComponent(offerId)}/lead-paint-disclosure`;

/**
 * Load the deal's lead-paint disclosure answers.
 *
 * @param {number|string} offerId
 * @returns {Promise<object>}
 */
export async function getLeadPaintDisclosure(offerId) {
  try {
    return await APIAgent.request(base(offerId), 'GET');
  } catch (err) {
    console.error('leadPaintDisclosureAgent.getLeadPaintDisclosure failed:', err);
    throw err;
  }
}

/**
 * Save the seller's answers. Text fields are only sent with the answer that
 * needs them.
 *
 * @param {number|string} offerId
 * @param {object} answers
 * @returns {Promise<object>}
 */
export async function saveSellerAnswers(offerId, answers) {
  const payload = {
    hazards_answer: answers?.hazards_answer,
    records_answer: answers?.records_answer,
    agent_answer: answers?.agent_answer,
    attested: Boolean(answers?.attested),
    records_delivery_confirmed: false,
  };
  if (payload.hazards_answer === HAZARDS.KNOWN) {
    payload.hazards_description = answers.hazards_description;
  }
  if (payload.records_answer === RECORDS.PROVIDED) {
    payload.records_description = answers.records_description;
    payload.records_delivery_confirmed = Boolean(answers.records_delivery_confirmed);
  }
  try {
    return await APIAgent.request(`${base(offerId)}/seller-answers`, 'PUT', payload);
  } catch (err) {
    console.error('leadPaintDisclosureAgent.saveSellerAnswers failed:', err);
    throw err;
  }
}

/**
 * Record that the buyer received the EPA pamphlet.
 *
 * @param {number|string} offerId
 * @returns {Promise<object>}
 */
export async function confirmPamphletReceipt(offerId) {
  try {
    return await APIAgent.request(`${base(offerId)}/pamphlet-receipt`, 'PUT', { received: true });
  } catch (err) {
    console.error('leadPaintDisclosureAgent.confirmPamphletReceipt failed:', err);
    throw err;
  }
}

/**
 * Regenerate the disclosure from the recorded answers (seller only), for one
 * rendered from older answers or without signature lines.
 *
 * @param {number|string} offerId
 * @returns {Promise<object>}
 */
export async function regenerateDisclosure(offerId) {
  try {
    return await APIAgent.request(`${base(offerId)}/regenerate`, 'POST');
  } catch (err) {
    console.error('leadPaintDisclosureAgent.regenerateDisclosure failed:', err);
    throw err;
  }
}

const LeadPaintDisclosureAPI = {
  getLeadPaintDisclosure,
  saveSellerAnswers,
  confirmPamphletReceipt,
  regenerateDisclosure,
};

export default LeadPaintDisclosureAPI;
